import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLogin(searchParams.get('mode') !== 'register');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('¡Bienvenido de nuevo!');
      } else {
        if (!name.trim()) {
          toast.error('Por favor, ingresa tu nombre');
          setLoading(false);
          return;
        }
        await register(email, password, name);
        toast.success('¡Cuenta creada exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Ha ocurrido un error';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Volver al inicio</span>
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/i0n29hu3_Exam%20Ready%20English%20Logo.png" 
              alt="Exam Ready English" 
              className="h-12 w-auto" 
            />
          </div>

          {/* Title */}
          <div className="mb-8 animate-fade-in-up">
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-slate-900 tracking-tight mb-2">
              {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h1>
            <p className="text-slate-600">
              {isLogin 
                ? 'Ingresa tus credenciales para continuar' 
                : 'Comienza tu preparación para Cambridge'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in-up animation-delay-100">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Nombre completo
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  className="mt-1.5 h-12 bg-white border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                  data-testid="name-input"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="mt-1.5 h-12 bg-white border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                data-testid="email-input"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Contraseña
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 bg-white border-slate-200 focus:border-orange-500 focus:ring-orange-500 pr-10"
                  data-testid="password-input"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {isLogin && (
                <div className="mt-2 text-right">
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-orange-600 hover:text-orange-700"
                    data-testid="forgot-password-link"
                  >
                    ¿Has olvidado tu contraseña?
                  </a>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-slate-900 text-white hover:bg-slate-800 rounded-full font-medium transition-all"
              data-testid="submit-btn"
            >
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </Button>
          </form>

          {/* Toggle */}
          <p className="mt-6 text-center text-slate-600">
            {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-orange-600 hover:text-orange-700 font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800">
          <img 
            src="https://images.unsplash.com/photo-1671539496156-f0511fc32bff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxjbGFzc2ljJTIwdW5pdmVyc2l0eSUyMGFyY2hpdGVjdHVyZSUyMGFic3RyYWN0JTIwY2FtYnJpZGdlfGVufDB8fHx8MTc2NjE0MDQ1N3ww&ixlib=rb-4.1.0&q=85"
            alt="Cambridge Architecture"
            className="w-full h-full object-cover opacity-60"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold mb-4">
              Tu camino hacia Cambridge
            </h2>
            <p className="text-lg text-white/80 max-w-md">
              Únete a miles de profesionales que han certificado su inglés con nuestra plataforma
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
